import { useState, useCallback, useRef, useMemo } from 'react'
import { useBulkSelection } from '@/shared/hooks/useBulkSelection'
import { useDebouncedCallback } from '@/shared/hooks/useDebouncedCallback'
import {
  resolveFilterVisitMode,
  useFilterVisitGuard,
  type FilterVisitMode,
} from '@/shared/hooks/useFilterVisitGuard'
import { useHydrateLocationFilters } from '@/shared/hooks/useHydrateLocationFilters'
import { router, usePage } from '@inertiajs/react'
import { clearLocationFilters, writeLocationFilters } from '@/shared/lib/locationFiltersSession'
import { clearNcPageFilters, writeNcPageFilters } from '@/shared/lib/ncPageFiltersSession'
import type { SharedProps } from '@/shared/types/SharedProps'
import {
  AlertTriangle,
  ArrowDownUp,
  Calendar,
  CheckSquare,
  ListChecks,
  Loader2,
  SlidersHorizontal,
  X,
} from 'lucide-react'
import { Button } from '@/shared/ui/button'
import { Badge } from '@/shared/ui/badge'
import { Checkbox } from '@/shared/ui/checkbox'
import { Input } from '@/shared/ui/input'
import { Label } from '@/shared/ui/label'
import { DatePickerInput } from '@/shared/ui/date-picker-input'
import { MultiSelectCombobox } from '@/shared/ui/multi-select-combobox'
import { Sheet, SheetContent, SheetTrigger, SheetClose } from '@/shared/ui/sheet'
import { Card, CardContent } from '@/shared/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog'
import { RichTextEditor } from '@/shared/ui/rich-text-editor'
import { CommentBody } from '@/features/comments/CommentsPanel'
import { richTextToPlainText } from '@/shared/lib/rich-text-html'
import DataPagination from '@/shared/ui/DataPagination'
import { MAX_COMMENTS_LENGTH, NonComplianceCard } from '@/features/non-compliance-actions'
import { NoAccessNotice } from '@/shared/ui/no-access-notice'
import { useCan } from '@/shared/hooks/useCan'
import { Resources, Actions } from '@/shared/rbac'
import { t } from '@/shared/lib/i18n'
import { appendArrayOrgFilterParams, clearOrgFiltersParams } from '@/shared/lib/persistedOrgFilters'
import {
  buildOrgCascadeChangeHandlers,
  visibleDepartmentOptionsForSites,
  visibleUnitOptionsForParents,
} from '@/shared/lib/orgMultiSelectCascade'
import { formatCalendarDate, parseCalendarDate } from '@/shared/lib/tenantDateTime'
import { useBreadcrumbs } from '@/shared/lib/breadcrumbs-context'
import {
  nonComplianceBulkReviewPath,
  nonComplianceDashboardPath,
  nonComplianceSelectableIdsPath,
} from '@/shared/lib/routes'
import DashboardTabs from '@/pages/Compliance/DashboardTabs'
import type { PaginationMeta } from '@/shared/types/PaginationMeta'
import type WebNonComplianceCard from '@/shared/types/web/WebNonComplianceCard'

const BULK_MAX_IDS = 50

const MS_PER_DAY = 1000 * 60 * 60 * 24

// oldestNcDate is a backend-canonical calendar day (YYYY-MM-DD). Age reflects that exact
// day rather than re-deriving it from a UTC-midnight instant in the browser timezone.
const oldestNcAgeInDays = (isoDate: string): number => {
  const oldest = parseCalendarDate(isoDate)
  if (!oldest) return 0
  const today = new Date()
  const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate())
  return Math.max(0, Math.floor((startOfToday.getTime() - oldest.getTime()) / MS_PER_DAY))
}

interface SelectOption {
  value: number
  label: string
  siteId?: number | null
  departmentId?: number | null
}

interface DateRange {
  from: string | null
  to: string | null
}

interface Filters {
  siteIds?: number[]
  departmentIds?: number[]
  unitIds?: number[]
  dateFrom?: string
  dateTo?: string
  search?: string
  sort?: string
}

interface Props {
  records: WebNonComplianceCard[]
  meta: PaginationMeta
  filters: Filters
  siteOptions: SelectOption[]
  departmentOptions: SelectOption[]
  unitOptions: SelectOption[]
  defaultDateRange: DateRange
  oldestNcDate: string | null
  currentSort: string
  [key: string]: unknown
}

const NonComplianceDashboardPage = () => {
  const {
    records,
    meta,
    filters,
    siteOptions,
    departmentOptions,
    unitOptions,
    defaultDateRange,
    oldestNcDate,
    currentSort,
  } = usePage<Props>().props
  const { currentTenantId, currentUserInfo } = usePage<SharedProps>().props
  const currentUserId = currentUserInfo?.id ?? null

  useBreadcrumbs([{ label: t('nav.dashboard') }, { label: t('nav.nonCompliance') }])

  const canReview = useCan(Resources.COMPLIANCE_REPORT, Actions.UPDATE)

  const sheetOpenRef = useRef(false)

  const [dismissingId, setDismissingId] = useState<number | null>(null)

  const [selectedSiteIds, setSelectedSiteIds] = useState<number[]>(filters.siteIds ?? [])
  const [selectedDeptIds, setSelectedDeptIds] = useState<number[]>(filters.departmentIds ?? [])
  const [selectedUnitIds, setSelectedUnitIds] = useState<number[]>(filters.unitIds ?? [])
  const [search, setSearch] = useState(filters.search || '')
  const [dateFrom, setDateFrom] = useState(filters.dateFrom || defaultDateRange.from || '')
  const [dateTo, setDateTo] = useState(filters.dateTo || defaultDateRange.to || '')
  const [isNavigating, setIsNavigating] = useState(false)

  const { beginVisit } = useFilterVisitGuard()
  const bulkSelection = useBulkSelection(BULK_MAX_IDS)
  const { clear: clearBulkSelection } = bulkSelection

  const navigate = useCallback(
    (
      overrides: {
        siteIds?: number[]
        departmentIds?: number[]
        unitIds?: number[]
        search?: string
        dateFrom?: string
        dateTo?: string
        sort?: string
        page?: number
      },
      mode: FilterVisitMode = 'default',
    ) => {
      const isPageChange = overrides.page != null
      const sites = overrides.siteIds ?? selectedSiteIds
      const depts = overrides.departmentIds ?? selectedDeptIds
      const units = overrides.unitIds ?? selectedUnitIds
      const q = (overrides.search ?? search).trim()
      const df = overrides.dateFrom ?? dateFrom
      const dt = overrides.dateTo ?? dateTo
      const sortValue = overrides.sort ?? currentSort

      const params = new URLSearchParams()
      appendArrayOrgFilterParams(params, {
        siteIds: sites,
        departmentIds: depts,
        unitIds: units,
      })
      if (q) params.set('search', q)
      if (df) params.set('date_from', df)
      if (dt) params.set('date_to', dt)
      if (sortValue && sortValue !== 'oldest') params.set('sort', sortValue)
      if (overrides.page != null) params.set('page', String(overrides.page))

      writeLocationFilters(currentTenantId, currentUserId, {
        siteIds: sites,
        departmentIds: depts,
        unitIds: units,
      })
      // NC-only bucket so Tasks → NC restores dates/search (T-P1). Not org-filters.
      writeNcPageFilters(currentTenantId, currentUserId, {
        dateFrom: df || null,
        dateTo: dt || null,
        search: q || null,
      })

      const query = params.size ? `?${params.toString()}` : ''
      const href = `${nonComplianceDashboardPath()}${query}`
      const visit = beginVisit(href, resolveFilterVisitMode(sheetOpenRef.current, mode))
      if (!visit) return

      // A selection commit keeps the page mounted, so drop bulk-selected cards the new filter
      // may hide instead of letting a bulk review act on them.
      if (visit.keepDropdownOpen) clearBulkSelection()
      setIsNavigating(true)
      router.visit(href, {
        // A cleared search remounts from server props so no stale typed query survives.
        // Pagination keeps the mounted page and must include the selection already on screen.
        preserveState: visit.keepDropdownOpen
          ? true
          : isPageChange || ('search' in overrides && q !== ''),
        preserveScroll: visit.keepDropdownOpen,
        onFinish: () => {
          if (visit.isCurrent()) setIsNavigating(false)
        },
      })
    },
    [
      selectedSiteIds,
      selectedDeptIds,
      selectedUnitIds,
      search,
      dateFrom,
      dateTo,
      currentSort,
      currentTenantId,
      currentUserId,
      beginVisit,
      clearBulkSelection,
    ],
  )

  useHydrateLocationFilters({
    tenantId: currentTenantId,
    userId: currentUserId,
    hasUrlLocationParams:
      (filters.siteIds?.length ?? 0) > 0 ||
      (filters.departmentIds?.length ?? 0) > 0 ||
      (filters.unitIds?.length ?? 0) > 0,
    currentFilters: {
      siteIds: filters.siteIds ?? [],
      departmentIds: filters.departmentIds ?? [],
      unitIds: filters.unitIds ?? [],
    },
    siteOptions,
    departmentOptions,
    unitOptions,
    onHydrate: (hydrated) => {
      setSelectedSiteIds(hydrated.siteIds)
      setSelectedDeptIds(hydrated.departmentIds)
      setSelectedUnitIds(hydrated.unitIds)
      navigate({
        siteIds: hydrated.siteIds,
        departmentIds: hydrated.departmentIds,
        unitIds: hydrated.unitIds,
      })
    },
  })

  const debouncedNavigate = useDebouncedCallback((searchValue: string) => {
    navigate({ search: searchValue })
  }, 400)

  const handleSearchChange = (value: string) => {
    if (value.trim() === '') {
      setSearch('')
      debouncedNavigate.cancel()
      navigate({ search: '' })
      return
    }
    setSearch(value)
    debouncedNavigate(value)
  }

  const orgCascade = buildOrgCascadeChangeHandlers({
    departmentOptions,
    unitOptions,
    selection: {
      siteIds: selectedSiteIds,
      departmentIds: selectedDeptIds,
      unitIds: selectedUnitIds,
    },
    applySelection: ({ siteIds, departmentIds, unitIds }) => {
      setSelectedSiteIds(siteIds)
      setSelectedDeptIds(departmentIds)
      setSelectedUnitIds(unitIds)
    },
  })

  const closeSheetWithCascadePrune = () => {
    debouncedNavigate.cancel()
    navigate(orgCascade.pruneSelection(), 'sheet-close')
  }

  const handleDateFromChange = (value: string) => {
    setDateFrom(value)
    navigate({ dateFrom: value })
  }

  const handleDateToChange = (value: string) => {
    setDateTo(value)
    navigate({ dateTo: value })
  }

  const filteredDepartmentOptions = useMemo(
    () => visibleDepartmentOptionsForSites(departmentOptions, selectedSiteIds),
    [departmentOptions, selectedSiteIds],
  )

  const filteredUnitOptions = useMemo(
    () => visibleUnitOptionsForParents(unitOptions, selectedSiteIds, selectedDeptIds),
    [unitOptions, selectedSiteIds, selectedDeptIds],
  )

  const handleSiteCommit = (newSiteIds: number[]) =>
    navigate(orgCascade.changeSites(newSiteIds), 'commit')

  const handleDeptCommit = (newDeptIds: number[]) =>
    navigate(orgCascade.changeDepartments(newDeptIds), 'commit')

  // ── Bulk select state ────────────────────────────────────────────────────────
  const [selectMultiple, setSelectMultiple] = useState(false)
  const [bulkComments, setBulkComments] = useState('')
  const [bulkProcessing, setBulkProcessing] = useState(false)
  const [selectAllLoading, setSelectAllLoading] = useState(false)
  const [pendingActionCode, setPendingActionCode] = useState<string | null>(null)
  const [bulkCommentsError, setBulkCommentsError] = useState(false)

  const exitSelectMode = () => {
    setSelectMultiple(false)
    bulkSelection.clear()
    setBulkComments('')
    setPendingActionCode(null)
  }

  const handleToggleCard = (id: number) => {
    bulkSelection.toggle(id)
  }

  const handleSelectAll = async (checked: boolean) => {
    if (!checked) {
      bulkSelection.clear()
      return
    }

    setSelectAllLoading(true)
    try {
      const params = new URLSearchParams()
      if (filters.siteIds) filters.siteIds.forEach((id) => params.append('site_id[]', String(id)))
      if (filters.departmentIds)
        filters.departmentIds.forEach((id) => params.append('department_id[]', String(id)))
      if (filters.unitIds) filters.unitIds.forEach((id) => params.append('unit_id[]', String(id)))
      if (filters.dateFrom) params.set('date_from', filters.dateFrom)
      if (filters.dateTo) params.set('date_to', filters.dateTo)
      if (filters.search) params.set('search', filters.search)
      if (currentSort && currentSort !== 'oldest') params.set('sort', currentSort)

      const response = await fetch(`${nonComplianceSelectableIdsPath()}?${params.toString()}`, {
        headers: { Accept: 'application/json' },
      })
      if (!response.ok) {
        bulkSelection.clear()
        return
      }
      const data = (await response.json()) as { ids: number[]; total: number }

      bulkSelection.selectAll(data.ids, data.total)
    } catch {
      bulkSelection.clear()
    } finally {
      setSelectAllLoading(false)
    }
  }

  const handleBulkSubmit = (reviewStatusCode: string) => {
    if (bulkSelection.selectedIds.size === 0 || bulkProcessing) return
    setBulkProcessing(true)
    router.post(
      nonComplianceBulkReviewPath(),
      {
        required_compliance_ids: Array.from(bulkSelection.selectedIds),
        review_status_code: reviewStatusCode,
        comments: bulkComments,
      },
      {
        onSuccess: () => exitSelectMode(),
        onFinish: () => setBulkProcessing(false),
      },
    )
  }

  const COMMENT_REQUIRED_CODES = ['ODE', 'CNM']

  const handleRequestBulkAction = (code: string) => {
    if (bulkSelection.selectedIds.size === 0) return
    if (COMMENT_REQUIRED_CODES.includes(code) && richTextToPlainText(bulkComments).length === 0) {
      setBulkCommentsError(true)
      return
    }
    setPendingActionCode(code)
  }

  const clearFilters = () => {
    debouncedNavigate.cancel()
    setSelectedSiteIds([])
    setSelectedDeptIds([])
    setSelectedUnitIds([])
    setSearch('')
    setDateFrom(defaultDateRange.from || '')
    setDateTo(defaultDateRange.to || '')
    clearLocationFilters(currentTenantId, currentUserId)
    // Drop NC date/search so Tasks → NC does not resurrect them after Clear (AC7 / T-P1).
    clearNcPageFilters(currentTenantId, currentUserId)
    const params = new URLSearchParams(clearOrgFiltersParams())
    const href = `${nonComplianceDashboardPath()}?${params.toString()}`
    const visit = beginVisit(href, 'default')
    if (!visit) return
    setIsNavigating(true)
    router.visit(href, {
      preserveState: false,
      onFinish: () => {
        if (visit.isCurrent()) setIsNavigating(false)
      },
    })
  }

  const goToPage = (page: number) => {
    debouncedNavigate.cancel()
    navigate({ page })
  }

  const activeFilterCount = [
    selectedSiteIds.length > 0,
    selectedDeptIds.length > 0,
    selectedUnitIds.length > 0,
    search,
    dateFrom && dateFrom !== (defaultDateRange.from ?? ''),
    dateTo,
  ].filter(Boolean).length

  const filterFields = (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3 lg:flex lg:flex-wrap lg:items-end">
      <div className="flex flex-col gap-1 sm:col-span-2 md:col-span-3 lg:col-span-1">
        <Label className="text-xs text-muted-foreground">
          {t('nonCompliance.filters.filterSearch')}
        </Label>
        <Input
          placeholder={t('nonCompliance.filters.searchPlaceholder')}
          value={search}
          onChange={(event) => handleSearchChange(event.target.value)}
          className="w-full lg:w-56"
        />
      </div>

      <div className="flex flex-col gap-1">
        <Label className="text-xs text-muted-foreground">
          {t('nonCompliance.filters.filterSite')}
        </Label>
        <MultiSelectCombobox
          options={siteOptions}
          selected={selectedSiteIds}
          onChange={orgCascade.changeSites}
          onClose={handleSiteCommit}
          placeholder={t('nonCompliance.filters.allSites')}
          searchPlaceholder={t('nonCompliance.filters.searchSites')}
          className="w-full lg:w-44"
          showBadges={false}
        />
      </div>

      <div className="flex flex-col gap-1">
        <Label className="text-xs text-muted-foreground">
          {t('nonCompliance.filters.filterDepartment')}
        </Label>
        <MultiSelectCombobox
          options={filteredDepartmentOptions}
          selected={selectedDeptIds}
          onChange={orgCascade.changeDepartments}
          onClose={handleDeptCommit}
          placeholder={t('nonCompliance.filters.allDepartments')}
          searchPlaceholder={t('nonCompliance.filters.searchDepartments')}
          className="w-full lg:w-44"
          showBadges={false}
        />
      </div>

      <div className="flex flex-col gap-1">
        <Label className="text-xs text-muted-foreground">
          {t('nonCompliance.filters.filterUnit')}
        </Label>
        <MultiSelectCombobox
          options={filteredUnitOptions}
          selected={selectedUnitIds}
          onChange={setSelectedUnitIds}
          onClose={(ids) => navigate({ unitIds: ids }, 'commit')}
          placeholder={t('nonCompliance.filters.allUnits')}
          searchPlaceholder={t('nonCompliance.filters.searchUnits')}
          className="w-full lg:w-40"
          showBadges={false}
        />
      </div>

      <div className="flex flex-col gap-1 min-w-0">
        <Label className="text-xs text-muted-foreground">
          {t('nonCompliance.filters.filterDateFrom')}
        </Label>
        <DatePickerInput
          value={dateFrom}
          onChange={(val) => handleDateFromChange(val)}
          className="w-full lg:w-40"
          allowTyping
        />
      </div>

      <div className="flex flex-col gap-1 min-w-0">
        <Label className="text-xs text-muted-foreground">
          {t('nonCompliance.filters.filterDateTo')}
        </Label>
        <DatePickerInput
          value={dateTo}
          onChange={(val) => handleDateToChange(val)}
          className="w-full lg:w-40"
          allowTyping
        />
      </div>
    </div>
  )

  return (
    <div className="space-y-4 sm:space-y-6">
      <DashboardTabs />
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-xl font-bold sm:text-2xl">{t('nonCompliance.dashboard.title')}</h1>

        <div className="lg:hidden">
          <Sheet
            onOpenChange={(open) => {
              sheetOpenRef.current = open
              if (open) {
                debouncedNavigate.cancel()
              } else {
                // Prune on close — MultiSelect onClose may not run (e.g. Escape).
                closeSheetWithCascadePrune()
              }
            }}
          >
            <SheetTrigger asChild>
              <Button variant="outline" size="sm" className="gap-2">
                <SlidersHorizontal className="h-4 w-4" />
                {t('nonCompliance.filters.searchButton')}
                {activeFilterCount > 0 && (
                  <Badge variant="secondary" className="ml-0.5 h-5 min-w-5 px-1 text-xs">
                    {activeFilterCount}
                  </Badge>
                )}
              </Button>
            </SheetTrigger>
            <SheetContent
              side="bottom"
              className="rounded-t-xl px-4 pt-4 pb-8"
              title={t('nonCompliance.filters.searchButton')}
              hideCloseButton
            >
              <div className="mb-4 flex items-center justify-between">
                <h2 className="font-semibold">{t('nonCompliance.filters.searchButton')}</h2>
                <div className="flex items-center gap-2">
                  {activeFilterCount > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-muted-foreground"
                      onClick={clearFilters}
                    >
                      {t('nonCompliance.filters.clear')}
                    </Button>
                  )}
                  <SheetClose asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={t('nonCompliance.filters.done')}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </SheetClose>
                </div>
              </div>
              <div className="space-y-3">{filterFields}</div>
            </SheetContent>
          </Sheet>
        </div>
      </div>

      <div className="hidden rounded-md border p-4 lg:block">
        <div className="flex flex-col gap-3">
          {filterFields}
          {activeFilterCount > 0 && (
            <div className="flex items-center lg:justify-end">
              <Button
                variant="ghost"
                size="sm"
                className="text-muted-foreground"
                onClick={clearFilters}
              >
                {t('nonCompliance.filters.clear')}
              </Button>
            </div>
          )}
        </div>
      </div>

      {isNavigating ? (
        <div
          className="flex flex-col items-center gap-3 py-16"
          data-testid="dashboard-filter-loading"
          role="status"
          aria-busy="true"
          aria-live="polite"
        >
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-hidden />
          <p className="text-sm text-muted-foreground">{t('nonCompliance.dashboard.loading')}</p>
        </div>
      ) : records.length === 0 ? (
        <div className="flex flex-col items-center gap-3 py-16 text-center">
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-muted">
            <CheckSquare className="h-5 w-5 text-muted-foreground" />
          </span>
          <p className="text-sm text-muted-foreground">{t('nonCompliance.dashboard.empty')}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {!canReview && <NoAccessNotice />}

          {/* Aging context + Sort toggle */}
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              {oldestNcDate && (
                <span className="flex items-center gap-1.5">
                  <Calendar className="h-3.5 w-3.5" />
                  {t('nonCompliance.dashboard.oldestNcLabel')}{' '}
                  <span className="font-medium text-foreground">
                    {formatCalendarDate(oldestNcDate)}
                  </span>
                  <Badge variant="outline" className="text-xs">
                    {t('nonCompliance.dashboard.ageDays').replace(
                      '{days}',
                      String(oldestNcAgeInDays(oldestNcDate)),
                    )}
                  </Badge>
                </span>
              )}
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="gap-1.5 text-xs"
              onClick={() => navigate({ sort: currentSort === 'oldest' ? 'newest' : 'oldest' })}
            >
              <ArrowDownUp className="h-3.5 w-3.5" />
              {currentSort === 'oldest'
                ? t('nonCompliance.dashboard.sortOldest')
                : t('nonCompliance.dashboard.sortNewest')}
            </Button>
          </div>

          {/* Select Multiple toggle (AC 1) */}
          {canReview && !selectMultiple && (
            <div className="flex justify-start">
              <Button
                variant="outline"
                size="sm"
                className="gap-2"
                onClick={() => setSelectMultiple(true)}
              >
                <ListChecks className="h-4 w-4" />
                {t('nonCompliance.bulk.selectMultiple')}
              </Button>
            </div>
          )}

          {/* Bulk action bar (AC 3) */}
          {canReview && selectMultiple && (
            <BulkActionBar
              selectedCount={bulkSelection.selectedIds.size}
              maxCount={BULK_MAX_IDS}
              totalCount={meta.total}
              allSelected={
                bulkSelection.selectedIds.size >= Math.min(meta.total, BULK_MAX_IDS) &&
                bulkSelection.selectedIds.size > 0
              }
              limitReached={bulkSelection.limitReached}
              selectAllCapped={bulkSelection.selectAllCapped}
              cappedTotal={bulkSelection.cappedTotal}
              selectAllLoading={selectAllLoading}
              processing={bulkProcessing}
              comments={bulkComments}
              commentsError={bulkCommentsError}
              onCommentsChange={(value) => {
                setBulkComments(value)
                if (bulkCommentsError) setBulkCommentsError(false)
              }}
              onSelectAll={handleSelectAll}
              onRequestSubmit={handleRequestBulkAction}
              onCancel={exitSelectMode}
            />
          )}

          <BulkConfirmDialog
            code={pendingActionCode}
            selectedCount={bulkSelection.selectedIds.size}
            comments={bulkComments}
            processing={bulkProcessing}
            onConfirm={() => {
              if (pendingActionCode) handleBulkSubmit(pendingActionCode)
              setPendingActionCode(null)
            }}
            onCancel={() => setPendingActionCode(null)}
          />

          {records.map((record) => (
            <NonComplianceCard
              key={record.id}
              record={record}
              isDismissing={dismissingId === record.id}
              onActionStart={setDismissingId}
              selectMode={selectMultiple}
              selected={bulkSelection.selectedIds.has(record.id)}
              onToggleSelect={handleToggleCard}
              canReview={canReview}
            />
          ))}
        </div>
      )}

      <DataPagination
        meta={meta}
        onPageChange={goToPage}
        noun={t('nonCompliance.dashboard.paginationNoun')}
      />
    </div>
  )
}

// ─── BulkActionBar ────────────────────────────────────────────────────────────

interface BulkActionBarProps {
  selectedCount: number
  maxCount: number
  totalCount: number
  allSelected: boolean
  limitReached: boolean
  selectAllCapped: boolean
  cappedTotal: number | null
  selectAllLoading: boolean
  processing: boolean
  comments: string
  commentsError: boolean
  onCommentsChange: (value: string) => void
  onSelectAll: (checked: boolean) => void
  onRequestSubmit: (code: string) => void
  onCancel: () => void
}

const BulkActionBar = ({
  selectedCount,
  maxCount,
  totalCount,
  allSelected,
  limitReached,
  selectAllCapped,
  cappedTotal,
  selectAllLoading,
  processing,
  comments,
  commentsError,
  onCommentsChange,
  onSelectAll,
  onCancel,
  onRequestSubmit,
}: BulkActionBarProps) => {
  const counterText = `${selectedCount} / ${maxCount} ${t('nonCompliance.bulk.selected')}`

  const commentsTextLength = richTextToPlainText(comments).length
  const commentsOverLength = commentsTextLength > MAX_COMMENTS_LENGTH

  const infoMessage = selectAllCapped
    ? t('nonCompliance.bulk.selectAllCapped')
        .replace('{max}', String(maxCount))
        .replace('{total}', String(cappedTotal))
    : limitReached
      ? t('nonCompliance.bulk.limitReached').replace('{max}', String(maxCount))
      : null

  return (
    <Card className="border-primary/30 bg-primary/5">
      <CardContent className="space-y-3 pt-4">
        {/* Header line with Select All + count + cancel */}
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Checkbox
              id="bulk-select-all"
              checked={allSelected}
              disabled={selectAllLoading || processing}
              onCheckedChange={(checked) => onSelectAll(Boolean(checked))}
              aria-label={t('nonCompliance.bulk.selectAll')}
            />
            <label htmlFor="bulk-select-all" className="text-sm font-medium cursor-pointer">
              {t('nonCompliance.bulk.selectAll')} ({Math.min(totalCount, maxCount)})
            </label>
            <Badge variant={limitReached ? 'destructive' : 'secondary'} className="tabular-nums">
              {counterText}
            </Badge>
          </div>
          <button
            type="button"
            className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
            onClick={onCancel}
          >
            {t('nonCompliance.bulk.cancel')} <X className="h-4 w-4" />
          </button>
        </div>

        {/* Info banner when limit is reached or select all was capped */}
        {infoMessage && (
          <div className="flex items-center gap-2 rounded-md bg-amber-50 px-3 py-2 text-xs font-medium text-amber-800 dark:bg-amber-950/50 dark:text-amber-300">
            <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
            {infoMessage}
          </div>
        )}

        {/* Action buttons row */}
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={processing || selectedCount === 0 || commentsOverLength}
            onClick={() => onRequestSubmit('ODE')}
          >
            {t('nonCompliance.bulk.override')}
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={processing || selectedCount === 0 || commentsOverLength}
            onClick={() => onRequestSubmit('NPT')}
          >
            {t('nonCompliance.bulk.noTesting')}
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={processing || selectedCount === 0 || commentsOverLength}
            onClick={() => onRequestSubmit('CNM')}
          >
            {t('nonCompliance.bulk.confirmNonCompliance')}
          </Button>
        </div>

        {/* Warning line */}
        <p className="text-xs font-medium text-amber-700 dark:text-amber-400">
          {t('nonCompliance.bulk.warning')}
        </p>

        {/* Comments textarea */}
        <RichTextEditor
          placeholder={t('nonCompliance.bulk.commentsPlaceholder')}
          value={comments}
          onChange={onCommentsChange}
          className={commentsOverLength || commentsError ? 'border-destructive' : undefined}
        />
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          {commentsError ? (
            <span className="text-destructive">
              {t('nonCompliance.bulk.commentsRequiredForAction')}
            </span>
          ) : (
            <span />
          )}
          <span className={commentsOverLength ? 'text-destructive' : ''}>
            {commentsTextLength} / {MAX_COMMENTS_LENGTH}
          </span>
        </div>
      </CardContent>
    </Card>
  )
}

// ─── BulkConfirmDialog ────────────────────────────────────────────────────────

const BULK_ACTION_CONFIRM_TITLE: Record<string, string> = {
  ODE: 'nonCompliance.bulk.confirm.titleOverride',
  NPT: 'nonCompliance.bulk.confirm.titleNoTesting',
  CNM: 'nonCompliance.bulk.confirm.titleConfirmNonCompliance',
}

interface BulkConfirmDialogProps {
  code: string | null
  selectedCount: number
  comments: string
  processing: boolean
  onConfirm: () => void
  onCancel: () => void
}

const BulkConfirmDialog = ({
  code,
  selectedCount,
  comments,
  processing,
  onConfirm,
  onCancel,
}: BulkConfirmDialogProps) => {
  const titleKey = code ? BULK_ACTION_CONFIRM_TITLE[code] : null

  return (
    <Dialog
      open={code !== null}
      onOpenChange={(open) => {
        if (!open) onCancel()
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {titleKey
              ? t(titleKey as Parameters<typeof t>[0]).replace('{count}', String(selectedCount))
              : null}
          </DialogTitle>
          <DialogDescription>{t('nonCompliance.bulk.confirm.body')}</DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {/* Warning */}
          <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 dark:border-amber-800 dark:bg-amber-950">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
            <p className="text-xs font-medium text-amber-700 dark:text-amber-400">
              {t('nonCompliance.bulk.warning')}
            </p>
          </div>

          {/* Explanation summary */}
          <div className="space-y-1">
            <p className="text-xs font-medium text-muted-foreground">
              {t('nonCompliance.bulk.confirm.explanationLabel')}
            </p>
            <div className="rounded-md bg-muted px-3 py-2 text-sm">
              {comments.replace(/<[^>]*>/g, '').trim() ? (
                <CommentBody body={comments} />
              ) : (
                <span className="italic text-muted-foreground">
                  {t('nonCompliance.bulk.confirm.noExplanation')}
                </span>
              )}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={processing}>
            {t('nonCompliance.bulk.confirm.cancelButton')}
          </Button>
          <Button onClick={onConfirm} disabled={processing}>
            {processing
              ? t('nonCompliance.bulk.submitting')
              : t('nonCompliance.bulk.confirm.confirmButton')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default NonComplianceDashboardPage
